import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import LoginModal from './LoginModal';
import LoginWithJobContext from './LoginWithJobContext';
import LoginWithBookingContext from './LoginWithBookingContext';
import LoginWithDashboardContext from './LoginWithDashboardContext';
import ContextualLoginPage from './ContextualLoginPage';
import UnauthorizedOverlay from './UnauthorizedOverlay';
import { useAuth } from './useAuth';
import { useTenant } from './useTenant';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredTenantPermission?: string;
  requiredJobPermission?: { jobId: number; permission: string };
  loginContext?: 'job' | 'booking' | 'dashboard' | 'assessment' | 'default';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredTenantPermission,
  requiredJobPermission,
  loginContext = 'default',
}) => {
  const { user, isAuthenticated } = useAuth();
  const tenant = useTenant();
  const params = useParams<{ jobId?: string }>();

  // Handle ?next redirect after successful authentication (e.g., from /docs/ auth flow)
  useEffect(() => {
    if (isAuthenticated) {
      const nextUrl = new URLSearchParams(window.location.search).get('next');
      if (nextUrl && nextUrl.startsWith('/docs/')) {
        window.location.href = nextUrl;
      }
    }
  }, [isAuthenticated]);

  // If not authenticated, show the appropriate login component based on context
  if (!isAuthenticated) {
    if (loginContext === 'job') {
      const jobId = params.jobId;
      if (jobId) {
        return <LoginWithJobContext jobId={jobId} />;
      }
      // If no jobId in params, fall back to default
      return <ContextualLoginPage />;
    }
    if (loginContext === 'booking') {
      return <LoginWithBookingContext />;
    }
    if (loginContext === 'dashboard') {
      return <LoginWithDashboardContext />;
    }
    if (loginContext === 'assessment') {
      return <ContextualLoginPage isAssessment />;
    }
    if (loginContext === 'default') {
      return <ContextualLoginPage />;
    }
    // Fallback to original modal
    return <LoginModal jobTitle="..." isDashboardLogin={false} />;
  }

  // Tenant-level permission check
  if (requiredTenantPermission) {
    if (
      !tenant ||
      !user?.tenant_roles[tenant.domain] ||
      user.tenant_roles[tenant.domain].role !== requiredTenantPermission
    ) {
      return (
        <UnauthorizedOverlay
          message={`You are logged in as ${user?.display}. You don't have permission to view this page.`}
        />
      );
    }
  }

  // Job-level permission check
  if (requiredJobPermission) {
    if (
      !tenant ||
      !user?.tenant_roles[tenant.domain] ||
      user.tenant_roles[tenant.domain].jobs[requiredJobPermission.jobId] !==
        requiredJobPermission.permission
    ) {
      return (
        <UnauthorizedOverlay
          message={`You are logged in as ${user?.display}. You don't have permission to view this job.`}
        />
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
